/**
 * Shared table sorting components for popup pages
 */
import React, { useCallback, useState } from 'react';
import { TableHead } from '@ais-ui/ui-core';
import { cn } from '@ais-ui/utils';
import { PiCaretDown, PiCaretUp, PiCaretUpDown } from 'react-icons/pi';

export interface TableSortState {
  orderBy: string;
  orderAscending: boolean;
}

export interface UsePopupTableSortOptions {
  defaultOrderBy?: string;
  defaultAscending?: boolean;
  onSort?: (orderBy: string, ascending: boolean) => void;
}

/**
 * Hook for managing table sorting state
 */
export function usePopupTableSort(options: UsePopupTableSortOptions = {}) {
  const { defaultOrderBy = 'name', defaultAscending = true, onSort } = options;

  const [orderBy, setOrderBy] = useState(defaultOrderBy);
  const [orderAscending, setOrderAscending] = useState(defaultAscending);

  const handleSort = useCallback(
    (column: string) => {
      const newAscending = orderBy === column ? !orderAscending : true;
      setOrderBy(column);
      setOrderAscending(newAscending);
      onSort?.(column, newAscending);
    },
    [orderBy, orderAscending, onSort],
  );

  return {
    orderBy,
    orderAscending,
    handleSort,
    setOrderBy,
    setOrderAscending,
  };
}

/**
 * Generic sorting function for arrays of objects
 */
export function sortByColumn<T extends Record<string, unknown>>(
  items: T[],
  orderBy: string,
  orderAscending: boolean,
  dateColumns: string[] = [],
): T[] {
  return [...items].sort((a, b) => {
    let aVal: unknown = a[orderBy];
    let bVal: unknown = b[orderBy];

    // Handle date fields
    if (dateColumns.includes(orderBy)) {
      aVal = aVal ? new Date(aVal as string | Date).getTime() : 0;
      bVal = bVal ? new Date(bVal as string | Date).getTime() : 0;
    }

    if (typeof aVal === 'string' && typeof bVal === 'string') {
      return orderAscending
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    }

    const aNum = Number(aVal) || 0;
    const bNum = Number(bVal) || 0;
    return orderAscending ? aNum - bNum : bNum - aNum;
  });
}

export interface SortableTableHeadProps {
  column: string;
  orderBy: string;
  orderAscending: boolean;
  onSort: (column: string) => void;
  children: React.ReactNode;
  className?: string;
  iconSize?: string;
  textSize?: string;
}

/**
 * Sortable table header component
 */
export const SortableTableHead: React.FC<SortableTableHeadProps> = ({
  column,
  orderBy,
  orderAscending,
  onSort,
  children,
  className = 'py-2 px-4',
  iconSize = 'size-4',
  textSize = 'text-sm',
}) => {
  return (
    <TableHead
      className={cn('cursor-pointer select-none', className)}
      onClick={() => onSort(column)}
    >
      <div className="flex items-center justify-between">
        <span className={textSize}>{children}</span>
        {orderBy === column ? (
          orderAscending ? (
            <PiCaretUp className={iconSize} />
          ) : (
            <PiCaretDown className={iconSize} />
          )
        ) : (
          <PiCaretUpDown className={cn(iconSize, 'opacity-50')} />
        )}
      </div>
    </TableHead>
  );
};
